import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { CommonButton } from '@/components/common'
import { formatDisplayTime } from '@/utils'
import PropTypes from 'prop-types'

export const ConfirmAppointmentButton = ({
  selectedSlot,
  loading,
  onClick,
  hasAppointments = false,
}) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [isAtBottom, setIsAtBottom] = useState(false)
  const sentinelRef = useRef(null)

  const showDesktopFixedFooter = isDesktop && hasAppointments

  useEffect(() => {
    if (!hasAppointments || isDesktop) {
      setIsAtBottom(false)
      return
    }

    let observer = null

    const timeoutId = setTimeout(() => {
      if (!sentinelRef.current) return

      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            setIsAtBottom(entry.isIntersecting)
          })
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.1,
        }
      )

      observer.observe(sentinelRef.current)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      if (observer && sentinelRef.current) {
        observer.unobserve(sentinelRef.current)
      }
    }
  }, [hasAppointments, isDesktop])

  const selectionSummary = useMemo(() => {
    if (!selectedSlot) return ''
    const firstAvailable = Array.isArray(selectedSlot.slots)
      ? selectedSlot.slots.find(s => s?.is_available)
      : null
    const time = firstAvailable?.start_time
      ? formatDisplayTime(firstAvailable.start_time, 'h:mm a')
      : ''
    const procedure = selectedSlot.procedure_name || selectedSlot.text1 || ''
    return [time, procedure].filter(Boolean).join(' • ')
  }, [selectedSlot])

  if (!hasAppointments) {
    return null
  }

  return (
    <>
      <Box
        ref={sentinelRef}
        sx={{
          height: '1px',
          width: '100%',
          mt: 0,
          pointerEvents: 'none',
        }}
      />
      {!isDesktop ? (
        <>
          <Box
            sx={{
              display: isAtBottom ? 'none' : 'block',
              height: '80px',
            }}
          />
          <Box
            sx={{
              mt: 2,
              px: 1.5,
              position: isAtBottom ? 'static' : 'fixed',
              bottom: isAtBottom ? 'auto' : 0,
              left: isAtBottom ? 'auto' : 0,
              right: isAtBottom ? 'auto' : 0,
              width: '100%',
              zIndex: isAtBottom ? 'auto' : 1000,
              backgroundColor: isAtBottom ? 'transparent' : 'background.paper',
              pb: 1.5,
              pt: 1.5,
              boxShadow: isAtBottom ? 'none' : '0 -2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <CommonButton
              id='confirm-appointment-btn'
              label='Confirm Appointment'
              variant='primary'
              size='medium'
              disabled={!selectedSlot}
              loading={loading}
              onClick={onClick}
              sx={{
                width: '100%',
                py: 1,
                typography: 'body2',
              }}
            />
          </Box>
        </>
      ) : null}

      {showDesktopFixedFooter ? (
        <>
          <Box sx={{ display: { md: 'block' }, height: { md: '88px' } }} />
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: theme => theme.zIndex.appBar,
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              px: 3,
              py: 1.5,
              backgroundColor: 'background.paper',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                width: 'min(720px, 100%)',
              }}
            >
              {selectedSlot ? (
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    Selected appointment
                  </Typography>
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectionSummary || 'Appointment selected'}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ flex: 1 }}
                >
                  Select an appointment time to continue
                </Typography>
              )}
              <CommonButton
                id='confirm-appointment-btn'
                label='Confirm Appointment'
                variant='primary'
                size='medium'
                disabled={!selectedSlot}
                loading={loading}
                onClick={onClick}
                sx={{
                  flexShrink: 0,
                  py: 1.25,
                  px: 3,
                }}
              />
            </Box>
          </Box>
        </>
      ) : null}
    </>
  )
}

ConfirmAppointmentButton.propTypes = {
  selectedSlot: PropTypes.object,
  loading: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  hasAppointments: PropTypes.bool,
}
